import Box from '@mui/material/Box';
import ImageListItem from '@mui/material/ImageListItem';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

import { EvidenceImage } from '../EvidenceImage/EvidenceImage';
import type { PhotoEvidenceItem, PhotoEvidenceListPhoto } from '../PhotoEvidenceList/PhotoEvidenceList';
import { Typography } from '../Typography/Typography';

type PhotoEvidenceListItemProps = {
  photo: PhotoEvidenceListPhoto;
  placeholderSrc?: string;
  openOnClick?: boolean;
  onClick?: (photo: PhotoEvidenceItem) => void;
  obfuscateAi?: boolean;
  valueCounter?: ReactNode;
  imageLoadingStrategy?: 'progressive' | 'eager';
  imageSx?: SxProps<Theme>;
  listItemSx?: SxProps<Theme>;
  footer?: ReactNode;
  placeholder?: ReactNode;
};

export const PhotoEvidenceListItem = ({
  photo,
  placeholderSrc,
  openOnClick,
  onClick,
  obfuscateAi,
  valueCounter,
  imageLoadingStrategy = 'progressive',
  imageSx,
  listItemSx,
  footer,
  placeholder,
}: PhotoEvidenceListItemProps) => {
  const {
    alt,
    thumbnailSrc,
    placeholderSrc: itemPlaceholderSrc,
    src,
    isLoading: isPhotoLoading,
    uploadProgress,
    cols,
    rows,
    imageRejectionReasons,
    imageWarningReasons,
    analysisStatus,
  } = photo;

  const hasRejectionReasons = imageRejectionReasons && imageRejectionReasons.length > 0;
  const shouldShowRejections = hasRejectionReasons;
  const shouldShowWarnings = false;

  return (
    <ImageListItem
      key={src}
      sx={[
        {
          breakInside: 'avoid',
          cursor: openOnClick ? 'pointer' : 'default',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          pageBreakInside: 'avoid',
        },
        ...(Array.isArray(listItemSx) ? listItemSx : listItemSx ? [listItemSx] : []),
      ]}
      cols={cols}
      rows={rows}
    >
      {valueCounter}
      <Box
        sx={{
          position: 'relative',
        }}
      >
        {src ? (
          <EvidenceImage
            alt={alt}
            src={thumbnailSrc || src}
            onClick={() => openOnClick && onClick?.(photo as PhotoEvidenceItem)}
            loading="lazy"
            placeholderSrc={itemPlaceholderSrc ?? placeholderSrc}
            isLoading={isPhotoLoading}
            uploadProgress={uploadProgress}
            analysisStatus={analysisStatus}
            obfuscateAi={obfuscateAi}
            loadingStrategy={imageLoadingStrategy}
            sx={imageSx}
          />
        ) : (
          placeholder
        )}
      </Box>
      {shouldShowRejections
        ? imageRejectionReasons?.map((reason) => (
            <Typography key={reason} variant="caption" color="error">
              {reason}
            </Typography>
          ))
        : null}
      {shouldShowWarnings
        ? imageWarningReasons?.map((reason) => (
            <Typography key={reason} variant="caption" color="warning">
              {reason}
            </Typography>
          ))
        : null}
      {footer}
    </ImageListItem>
  );
};
